import { Injectable } from '@angular/core';

import { Observable, catchError, filter, of, switchMap, take, tap } from 'rxjs';

import { BlockedTime } from '@shared/blocked-time/models';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { ConfirmationModalService } from '@tablesready/common-ui';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { BlockedTimeApiService } from './blocked-time-api.service';
import {
  AddEditBlockedTimeComponent,
  BlockedTimeModalResult,
} from '../modals/add-edit-blocked-time/add-edit-blocked-time.component';

export interface BlockedTimeEditorContext {
  services: Service[];
  resources: Resource[];
  timezone: string;
  blockedTimes: BlockedTime[];
}

@Injectable({ providedIn: 'root' })
export class BlockedTimeActionsService {
  constructor(
    private readonly modalController: FrontendModalService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly alertsService: AlertsService,
    private readonly blockedTimeApiService: BlockedTimeApiService,
  ) {}

  openEditor(
    blockedTime: Partial<BlockedTime>,
    context: BlockedTimeEditorContext,
  ): Observable<unknown> {
    return this.modalController
      .create<AddEditBlockedTimeComponent, BlockedTimeModalResult>(
      AddEditBlockedTimeComponent,
      {
        blockedTime,
        services: context.services,
        resources: context.resources,
        timezone: context.timezone,
        blockedTimes: context.blockedTimes,
      },
      FrontendModalSize.wider,
    )
      .pipe(
        take(1),
        filter((result): result is BlockedTimeModalResult => Boolean(result)),
        switchMap((result) => {
          if (result.action === 'delete' && blockedTime._id) {
            return this.confirmAndDelete(blockedTime);
          }

          if (result.blockedTime) {
            return this.save(result.blockedTime);
          }

          return of(null);
        }),
      );
  }

  confirmAndDelete(blockedTime: Partial<BlockedTime>): Observable<unknown> {
    return this.confirmationModalService
      .confirm({
        title: 'Delete blocked time',
        content: `Are you sure you want to delete ${blockedTime.name}?`,
        closeBtnText: 'Cancel',
        confirmBtnText: 'Delete',
      })
      .pipe(
        take(1),
        filter(result => result?.isConfirmed),
        switchMap(() => this.blockedTimeApiService.deleteById(blockedTime._id)),
        tap(() => {
          this.notify(IAlertTypes.success, 'Deleted:', 'blocked time removed');
        }),
        catchError(error => this.notifyError(error)),
      );
  }

  private save(payload: Partial<BlockedTime>): Observable<unknown> {
    const { _id, ...rest } = payload;
    const request = _id
      ? this.blockedTimeApiService.updateById(_id, rest)
      : this.blockedTimeApiService.create(rest);

    return request.pipe(
      tap(() => {
        this.notify(IAlertTypes.success, 'Success:', 'saved blocked time');
      }),
      catchError(error => this.notifyError(error)),
    );
  }

  private notify(type: IAlertTypes, heading: string, body: string): void {
    this.alertsService.add({
      type,
      heading,
      body,
    });
  }

  private notifyError(error: unknown): Observable<null> {
    this.alertsService.add({
      type: IAlertTypes.danger,
      heading: 'Error:',
      body: this.extractErrorMessage(error),
    });

    return of(null);
  }

  private extractErrorMessage(error: unknown): string {
    const apiMessage = (error as { error?: { message?: string | string[] } })?.error?.message;
    if (Array.isArray(apiMessage)) {
      return apiMessage.join(', ');
    }
    if (apiMessage) {
      return apiMessage;
    }

    return error instanceof Error ? error.message : String(error);
  }
}
